import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
} from '@mui/material';
import { ColorRing } from 'react-loader-spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { RenderFields } from './RenderFields';
import VirtualizedAutocomplete from '../../../../components/Dropdown/VirtualizedAutocomplete';
import { AlertContext } from '../../../../components/CustomFunctions/AlertContext';
import { createContract } from './services/maintenanceContractService';

import {
  contractFields
} from './maintenanceContractFields';


const AddContractModal = ({
  open,
  handleClose,
  updateList,
  allOptions,
}) => {
  const { alert } = useContext(AlertContext);
  const [contractData, setContractData] = useState({ contractStatus: "Active" });
  const [isLoading, setIsLoading] = useState(false);

  const [buildingLabel, setBuildingLabel] = useState("N/A");
  const [options, setOptions] = useState(allOptions || {});

  useEffect(() => {

    setOptions(allOptions || {});
  }, [allOptions])


  const handleSave = async () => {
    const data = { ...contractData, deviceContractsDtos: [] };

    if (!data.buildingId) {
      alert('Building Site is required', true);
      return;
    }


    setIsLoading(true);

    try {
      const response = await createContract(data);
      alert('Contract created successfully');
      updateList((prev) => [response, ...prev]);
      setContractData({});
      setBuildingLabel("N/A");
      handleClose();
    }
    catch (error) {
      alert('Error creating contract', true);
    }
    finally {
      setIsLoading(false);
    }
  };



  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setContractData((prev) => ({ ...prev, [name]: value }));

  };


  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };


  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: '90%',
            maxHeight: '90vh',
            minHeight: '90vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-2">
            {/* Left section: Back button and title */}
            <div className="flex items-center space-x-2 flex-grow">
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon fontSize='small' />
              </button>
              <h2 className="text-xl font-bold text-left">
                Add Contract
              </h2>
            </div>
          </div>
          <div className="flex justify-end space-x-2 p-2">
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm bg-white text-sss-blue-sel font-bold rounded-md border border-sss-blue-sel hover:bg-blue-50 transition"
            >
              Save
            </button>
          </div>
        </div>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[500px]">


            <ColorRing
              visible={isLoading}
              height="200"
              width="200"
              ariaLabel="color-ring-loading"
              colors={['#033B5C', '#033B5C', '#033B5C', '#033B5C', '#033B5C']}
            />


          </div>
        ) : (

          <DialogContent dividers>
            <div className="p-4 space-y-5 rounded-2xl">

              <div className="flex justify-between items-center w-full pb-0">
                {/* Building Site Section (Left Side) */}
                <div className="flex items-center w-1/2">
                  <label className="w-1/3 h-10 flex items-center bg-gray-200 px-4 py-2 text-left font-bold text-sss-blue-sel border border-gray-300"
                    style={{ lineHeight: '1.2', wordBreak: 'break-word' }}>
                    Building Site
                  </label>
                  <VirtualizedAutocomplete
                    options={options?.buildingId || []}
                    value={{ buildingId: contractData["buildingId"], buildingLabel: buildingLabel }}
                    disabled={false}
                    name="buildingId"
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleFieldChange({ target: { name: "buildingId", value: newValue.buildingID } });
                        setBuildingLabel(`${newValue?.buildingID} - ${newValue?.buildingReference}`);
                      } else {
                        handleFieldChange({ target: { name: "buildingId", value: null } });
                        setBuildingLabel(null);
                      }
                    }}
                    optionKey="buildingId"
                    optionLabel="buildingLabel"
                    renderOption={(props, option, state) => (
                      <li {...props} key={option?.buildingID} value={option?.buildingID}>
                        {option?.buildingID} - {option?.buildingReference}
                      </li>
                    )}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase();
                      if (inputValue.trim() === '' || inputValue === null) return options;
                      return options.filter((option) =>
                        (String(option?.buildingID) || '').toLowerCase().includes(inputValue) ||
                        (option.buildingReference || '').toString().toLowerCase().includes(inputValue)
                      );
                    }}
                    className="h-10 border border-gray-300 flex-grow px-4 py-2"
                  />
                </div>

                {/* Status Dropdown (Right Side) */}
                <div className="flex items-center space-x-2">
                  <label className="font-medium text-gray-700">Status:</label>
                  <select
                    className={`border p-2 rounded-md font-medium transition-colors
                              ${contractData.contractStatus === "Active" ? "text-green-700" : "text-red-700"}`}
                    value={contractData.contractStatus}
                    onChange={(e) => handleFieldChange({ target: { name: "contractStatus", value: e.target.value } })}
                  >
                    <option value="Active" className="text-green-700">Active</option>
                    <option value="Pending" className="text-red-700">Pending</option>
                    <option value="Expired" className="text-red-700">Expired</option>
                    <option value="Cancelled" className="text-red-700">Cancelled</option>
                  </select>
                </div>

              </div>


              {(() => {
                const [generalInfoLeft, generalInfoRight] = splitFields(
                  contractFields
                );
                return (

                  <div
                    className='flex flex-row gap-4'
                  >
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoLeft}
                        formData={contractData}
                        handleChange={handleFieldChange}
                        readOnly={false}
                        allOptions={options}

                      />
                    </div>
                    <div className="w-full">
                      <RenderFields
                        fields={generalInfoRight}
                        formData={contractData}
                        handleChange={handleFieldChange}
                        readOnly={false}
                        allOptions={options}

                      />
                    </div>

                  </div>


                );
              })()}
            </div>


          </DialogContent>
        )}

      </Dialog>
    </>
  );
};

AddContractModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateList: PropTypes.func.isRequired,
  allOptions: PropTypes.object,
};



export default AddContractModal;
